import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import { mmse } from 'neuro-schemas';
import * as React from 'react';
import { useIntl } from 'react-intl';
import colors from '../../../../../config/theme/colors';
import { mmseConfig } from './config';
import { referenceText } from '../../../../../config/theme/componentTheme';

const { calculateScore } = mmse;

const MMSEForm = ({ formData, viewing }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const optionFormatter =
    (opt: number | string) =>
    (name: number | string): string => {
      return fm(`cognition.mmse.${opt}.${name}`);
    };

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="cognition.unableToPerform">
        <InputHandler
          type="CheckboxSingle"
          editing={!viewing}
          name="unableToPerform"
          formData={formData}
          option={'true'}
          showLabel={false}
          optionFormatter={optionFormatter('unableToPerform')}
          dependentFieldsList={(): string[] => [
            'unableToPerformReason',
            'totalScoreOnly',
            'totalScore',
            'year',
            'season',
            'dayOfMonth',
            'dayOfWeek',
            'month',
            'country',
            'region',
            'city',
            'place',
            'floor',
            'words1',
            'substraction',
            'words2',
            'items',
            'repeatSentence',
            'paper',
            'text',
            'writeSentence',
            'draw',
          ]}
        />
      </FormRow>
      <FormRow title="general.reason" condition={formData.document.unableToPerform === true}>
        <InputHandler
          type="TextArea"
          name="unableToPerformReason"
          formData={formData}
          placeholder="general.reason"
          editing={!viewing}
          width={40}
        />
      </FormRow>
      {formData.document.unableToPerform !== true ? (
        <>
          <FormRow title="cognition.totalScoreOnly">
            <InputHandler
              type="Checkbox"
              editing={!viewing}
              name="totalScoreOnly"
              formData={formData}
              options={['totalScoreOnly']}
              optionFormatter={(name: number | string): string => fm(`cognition.${name}`)}
            />
          </FormRow>
          <FormRow title="cognition.download">
            <a href="/files/MMSE-Test-Finnish.pdf" target="_blank" rel="noopener noreferrer">
              {fm('cognition.downloadHere')}
            </a>
          </FormRow>
          {formData.document.totalScoreOnly?.[0] === 'totalScoreOnly' ? (
            <FormSection>
              <FormRow title="cognition.totalScore">
                <InputHandler
                  type="NumberField"
                  editing={!viewing}
                  name="totalScore"
                  formData={formData}
                  placeholder="cognition.totalScore"
                  staticValue={formData.document.totalScore}
                  min={0}
                  max={30}
                />
              </FormRow>
            </FormSection>
          ) : (
            <>
              <FormSection>
                {mmseConfig.map((obj: { name: string; options: string[]; description: boolean }) => (
                  <FormRow
                    title={`cognition.mmse.${obj.name}.title`}
                    description={obj.description === true ? fm(`cognition.mmse.${obj.name}.description`) : undefined}
                    key={obj.name}
                  >
                    <InputHandler
                      type="Checkbox"
                      editing={!viewing}
                      name={obj.name}
                      formData={formData}
                      options={obj.options}
                      optionFormatter={optionFormatter(obj.name)}
                    />
                  </FormRow>
                ))}
              </FormSection>
              <FormSection>
                <FormRow title="cognition.totalScore">
                  <div
                    style={{
                      fontSize: '2rem',
                      fontWeight: 600,
                      color: colors.primary,
                      marginRight: '2rem',
                      marginLeft: '2lem',
                    }}
                  >
                    {calculateScore(formData.document) ?? '-'}
                  </div>
                </FormRow>
                <div style={referenceText}>{fm('cognition.mmse.referenceText')}</div>
              </FormSection>
              <FormSection>
                <FormRow title="cognition.additionalInfo">
                  <InputHandler
                    name="additionalInfo"
                    type="TextArea"
                    editing={!viewing}
                    placeholder={fm('cognition.additionalInfo')}
                    width={50}
                    formData={formData}
                  />
                </FormRow>
              </FormSection>
            </>
          )}
        </>
      ) : undefined}
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'mmse'>>;
  viewing?: boolean;
}

export default MMSEForm;
